import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H2, H3 } from "../../components/Heading";
import { Loader } from "../../components/Loader";
import { useAllAdditionalDocuments } from "../../data/useAllAdditionalDocuments";
import { Paragraph } from "../../components/Paragraph";
import { Image } from "../../components/Image";
import editIcon from "../../assets/settings/edit.svg";
import { AddEditAdditionalDocument } from "../../components/Modals/AddEditAdditionalDocument";
import { useEffect, useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa";
import CustomTabs from "../../components/CustomTabs";
import { HiOutlineUser, HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { useBillingInfo } from "../../data/useBillingInfo";

type TypeFilter = "individual" | "entity";

type AdditionalDoc = {
  id: number;
  name: string;
  description?: string;
  _type: TypeFilter;
  active?: boolean;
  is_global?: boolean;
  document_capture_type?: Array<{
    id: number;
    name: string;
    slug_name: string;
  }>;
};

const SettingsAdditionalDocumentsRequests = () => {
  const { data: billingData, isLoading: billingInfoLoading } = useBillingInfo();
  const billingInfo =
    billingData?.data?.results?.length > 0 ? billingData.data.results[0] : null;

  const profilesEnabled = Boolean(billingInfo?.profiles_enabled);

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!profilesEnabled && activeTab !== 0) setActiveTab(0);
  }, [profilesEnabled, activeTab]);

  const viewType: TypeFilter =
    profilesEnabled && activeTab === 1 ? "entity" : "individual";

  const { data, isLoading } = useAllAdditionalDocuments(viewType);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null,
  );

  const addAdditionalDoc = () => {
    setIsEditModalOpen(true);
    setSelectedDocumentId(null);
  };

  const handleEdit = (id: number) => {
    setIsEditModalOpen(true);
    setSelectedDocumentId(id);
  };

  // const allDocs = useMemo<AdditionalDoc[]>(
  //   () => (data ?? []) as AdditionalDoc[],
  //   [data],
  // );

  // const filtered = useMemo(
  //   () => allDocs.filter((d) => d?._type === viewType),
  //   [allDocs, viewType],
  // );
  const docs = useMemo<AdditionalDoc[]>(
    () => (data ?? []) as AdditionalDoc[],
    [data],
  );
  const tabs = useMemo(() => {
    const base = [
      {
        label: (
          <>
            <HiOutlineUser size={16} />
            <span>Individual</span>
          </>
        ),
        content: null,
      },
    ];

    if (!profilesEnabled) return base;

    return [
      ...base,
      {
        label: (
          <>
            <HiOutlineBuildingOffice2 size={16} />
            <span>Entity</span>
          </>
        ),
        content: null,
      },
    ];
  }, [profilesEnabled]);

  const pageLoading = isLoading || billingInfoLoading;

  return (
    <>
      <Box bg="white" borderRadius={0} p={3} mb={3}>
        <Flex justifyContent={"space-between"} alignItems={"center"} mb={2}>
          <H2 mb={0}>Manage additional document requests</H2>

          <Button
            Icon={FaPlus}
            iconSize="16px"
            onClick={addAdditionalDoc}
            variant="primary"
            type="button"
            fontWeight={"bold"}
          >
            Add additional document
          </Button>
        </Flex>

        <Paragraph color={"gray.60"} fontSize={1} mt={"0"} mb={3}>
          Customise requests for additional documents when more information or
          supporting materials are needed.
        </Paragraph>

        <CustomTabs
          activeTab={activeTab}
          onTabChange={(i: number) => setActiveTab(i)}
          customMarginLeft="0"
          variant="segmented"
          tabs={tabs}
        />

        {pageLoading ? (
          <Flex justifyContent="center" alignItems="center" minHeight="35vh">
            <Loader />
          </Flex>
        ) : docs.length === 0 ? (
          <Paragraph color="gray.60" mt={2}>
            No {viewType} additional documents found.
          </Paragraph>
        ) : (
          docs.map((item) => (
            <Flex
              key={item.id}
              py={2}
              borderBottom="1px solid #e0e0e0"
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Box>
                <H3>{item.name}</H3>
                {item.description && (
                  <Paragraph my={0} color="gray.60">
                    {item.description}
                  </Paragraph>
                )}
              </Box>
              <Box>
                <Image
                  src={editIcon}
                  alt="edit icon"
                  height="24px"
                  width="24px"
                  marginRight={"4px"}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleEdit(item.id)}
                />
              </Box>
            </Flex>
          ))
        )}
      </Box>

      <AddEditAdditionalDocument
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        documentId={selectedDocumentId}
      />
    </>
  );
};

export default SettingsAdditionalDocumentsRequests;
